import { type CodeList, parseCodeList } from "./genericode";
import { assert } from "./project";
import { FieldMessage, GroupMessage, SchemaMessage } from "./xdatenfelder/v2";

export type FullSchemaOut = {
  fim_id: string;
  fim_version: string;
  xdf_version: string;
  datenfelder: FieldSearchResultItem[];
};

export type ChildReference = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  anzahl: string;
  type: "Feld" | "Gruppe";
};

export type GroupSearchResultItem = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  name: string;
};

export type FieldSearchResultItem = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  name: string;
  code_list_id: number | null;
  feldart: "input" | "select" | "label" | "hidden" | "locked";
};

export type GroupWithChildren = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  name: string;
  children: ChildReference[];
};

export type DetailedGroup = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  xdf_version: string;
  name: string;
  beschreibung: string | null;
  definition: string | null;
  bezug: string[];
  freigabe_status: number;
  status_gesetzt_am: string | null;
  status_gesetzt_durch: string | null;
  gueltig_ab: string | null;
  gueltig_bis: string | null;
  versionshinweis: string | null;
  veroeffentlichungsdatum: string | null;
  children: ChildReference[];
  datenfelder: FieldSearchResultItem[];
  datenfeldgruppen: GroupWithChildren[];
};

export type DetailedField = {
  namespace: string;
  fim_id: string;
  fim_version: string;
  xdf_version: string;
  name: string;
  beschreibung: string | null;
  definition: string | null;
  bezug: string[];
  freigabe_status: number;
  status_gesetzt_am: string | null;
  status_gesetzt_durch: string | null;
  gueltig_ab: string | null;
  gueltig_bis: string | null;
  versionshinweis: string | null;
  veroeffentlichungsdatum: string | null;
  feldart: "input" | "select" | "label" | "hidden" | "locked";

  code_list: {
    id: number;
  } | null;
};

export type SearchResult<T> = {
  items: T[];
  total_count: number;
};

/**
 * Load the full schema message including all contained code lists.
 */
export async function loadFullSchemaMessage(fimId: string, fimVersion: string) {
  const schema = await loadSchema(fimId, fimVersion);
  if (schema === null) {
    throw new Error("Schema not found");
  }

  if (schema.xdf_version !== "2.0") {
    throw new Error(`Unsupported XDF version: ${schema.xdf_version}`);
  }

  const codeLists = await importCodeLists(schema.datenfelder);

  const xml = await loadSchemaXdf(fimId, fimVersion);
  const message = SchemaMessage.fromString(xml!);

  return {
    message,
    codeLists,
  };
}

/**
 * Load the full group message including all contained code lists.
 */
export async function loadFullGroupMessage(fimId: string, fimVersion: string) {
  const group = await loadGroupDetails(fimId, fimVersion);
  if (group === null) {
    throw new Error("Group not found");
  }

  if (group.xdf_version !== "2.0") {
    throw new Error(`Unsupported XDF version: ${group.xdf_version}`);
  }

  const message = await loadGroupMessage(fimId, fimVersion);
  assert(message !== null);

  const codeLists: Record<string, CodeList> = await importCodeLists(
    group.datenfelder,
  );

  return {
    message,
    codeLists,
  };
}

/**
 * Load the full group message including all contained code lists.
 */
export async function loadFullFieldMessage(fimId: string, fimVersion: string) {
  const field = await loadFieldDetails(fimId, fimVersion);
  if (field === null) {
    throw new Error("Field not found");
  }

  if (field.xdf_version !== "2.0") {
    throw new Error(`Unsupported XDF version: ${field.xdf_version}`);
  }

  const message = await loadFieldMessage(fimId, fimVersion);
  assert(message !== null);

  const codeLists: Record<string, CodeList> = {};
  if (field.code_list !== null) {
    const codeList = await importCodeList(field.code_list.id);
    if (codeList !== null) {
      codeLists[codeList.identifier.canonicalVersionUri] = codeList;
    }
  }

  return {
    message,
    codeLists,
  };
}

async function importCodeLists(
  fields: FieldSearchResultItem[],
): Promise<Record<string, CodeList>> {
  const codeLists: Record<string, CodeList> = {};
  for (const field of fields) {
    if (field.code_list_id === null) {
      continue;
    }

    const codeList = await importCodeList(field.code_list_id);
    if (codeList !== null) {
      codeLists[codeList.identifier.canonicalVersionUri] = codeList;
    }
  }

  return codeLists;
}

async function importCodeList(codeListId: number): Promise<CodeList | null> {
  const codeListXML = await loadCodeListXML(codeListId);
  if (codeListXML === null) {
    console.warn(`Codeliste ${codeListId} konnte nicht gefunden werden.`);
    return null;
  }

  return parseCodeList(codeListXML);
}

export async function loadAllSchemas(): Promise<[string, string][]> {
  const schemaIdentifiers: [string, string][] = [];

  while (true) {
    const url = `https://neu.fimportal.de/api/v1/schemas?offset=${schemaIdentifiers.length}`;
    const response = await get(url);
    if (response.status !== 200) {
      throw new Error(
        `Failed to fetch schema identifiers: ${response.status} ${response.text}`,
      );
    }

    const result = await response.json();
    for (const schema of result.items) {
      schemaIdentifiers.push([schema.fim_id, schema.fim_version]);
    }

    if (schemaIdentifiers.length === result.total_count) {
      return schemaIdentifiers;
    }
  }
}

export async function loadCodeListXML(id: number): Promise<string | null> {
  const url = `https://neu.fimportal.de/immutable/code-lists/${id}/genericode.xml`;

  const response = await get(url);

  if (response.ok) {
    return await response.text();
  } else if (response.status === 404) {
    return null;
  } else {
    console.error(response.status, await response.text());
    return null;
  }
}

export async function loadSchema(
  fimId: string,
  fimVersion: string,
): Promise<FullSchemaOut | null> {
  const url = `https://neu.fimportal.de/api/v1/schemas/${fimId}/${fimVersion}`;

  const response = await get(url);

  if (!response.ok) {
    console.error(response.status, response.text);
    return null;
  } else {
    return await response.json();
  }
}

export async function loadSchemaXdf(
  fimId: string,
  fimVersion: string,
): Promise<string | null> {
  const url = `https://neu.fimportal.de/api/v1/schemas/${fimId}/${fimVersion}/xdf`;
  const response = await get(url);

  if (response.status === 404) {
    return null;
  } else if (response.ok) {
    return await response.text();
  } else {
    throw new Error(
      `Failed to fetch schema XDF: ${response.status} ${response.text}`,
    );
  }
}

export async function loadGroupMessage(
  fimId: string,
  fimVersion: string,
): Promise<GroupMessage | null> {
  const url = `https://neu.fimportal.de/api/v1/groups/baukasten/${fimId}/${fimVersion}/xdf`;
  const response = await get(url);

  if (response.status === 404) {
    return null;
  } else if (response.ok) {
    return GroupMessage.fromString(await response.text());
  } else {
    throw new Error(
      `Failed to fetch group message: ${response.status} ${response.text}`,
    );
  }
}

export async function loadGroupDetails(
  fimId: string,
  fimVersion: string,
): Promise<DetailedGroup> {
  const url = `https://neu.fimportal.de/api/v1/groups/baukasten/${fimId}/${fimVersion}`;

  const response = await get(url);
  const result = await response.json();

  return result;
}

export async function searchGroups(
  name: string,
  limit: number,
): Promise<SearchResult<GroupSearchResultItem>> {
  const url = `https://neu.fimportal.de/api/v1/groups?name=${name}&is_latest=true&limit=${limit}`;

  const response = await get(url);
  const result = await response.json();

  return result;
}

export async function loadFieldMessage(
  fimId: string,
  fimVersion: string,
): Promise<FieldMessage | null> {
  const url = `https://neu.fimportal.de/api/v1/fields/baukasten/${fimId}/${fimVersion}/xdf`;
  const response = await get(url);

  if (response.status === 404) {
    return null;
  } else if (response.ok) {
    return FieldMessage.fromString(await response.text());
  } else {
    throw new Error(
      `Failed to fetch field message: ${response.status} ${response.text}`,
    );
  }
}

export async function loadFieldDetails(
  fimId: string,
  fimVersion: string,
): Promise<DetailedField> {
  const url = `https://neu.fimportal.de/api/v1/fields/baukasten/${fimId}/${fimVersion}`;

  const response = await get(url);
  const result = await response.json();

  return result;
}

export async function searchFields(
  name: string,
  limit: number,
): Promise<SearchResult<FieldSearchResultItem>> {
  const url = `https://neu.fimportal.de/api/v1/fields?name=${name}&is_latest=true&limit=${limit}`;

  const response = await get(url);
  const result = await response.json();

  return result;
}

async function get(url: string, maxRetries = 5): Promise<Response> {
  while (maxRetries > 0) {
    try {
      const response = await fetch(url);

      if (response.status === 429) {
        maxRetries -= 1;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else {
        return response;
      }
    } catch (error) {
      maxRetries -= 1;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  throw new Error(`Failed to fetch ${url}: Too many requests`);
}
